import {
  Box,
  Button,
  Checkbox,
  CloseIcon,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  MuiDialogTitle,
  SyncIcon,
  Theme,
  Typography,
  makeStyles,
  useTheme,
} from '@forgeglobal/design-framework'
import { ListingStructureType, OrderBookFilterInput } from 'graphql/bindings/AdminBindings'
import { StyleDefaults } from 'isolated-design-library'
import { listingStructureDisplayName } from 'util/OpportunityUtils'
import React, { useState } from 'react'

const volumeSize = [
  { label: '<1M', value: { min: 0, max: 1000000 } },
  { label: '$1M-5M', value: { min: 1000000, max: 5000000 } },
  { label: '$5M-10M', value: { min: 5000000, max: 10000000 } },
  { label: '$10M-50M', value: { min: 10000000, max: 50000000 } },
  { label: '$50M+', value: { min: 50000000, max: 1000000000 } },
]

const valuationSize = [
  { label: '<100M', value: { min: 0, max: 100000000 } },
  { label: '$100M-500M', value: { min: 100000000, max: 500000000 } },
  { label: '$500M-1B', value: { min: 500000000, max: 1000000000 } },
  { label: '$1B-5B', value: { min: 1000000000, max: 5000000000 } },
  { label: '$5B-10B', value: { min: 5000000000, max: 10000000000 } },
  { label: '10B+', value: { min: 10000000000, max: 100000000000 } },
]

const useStyles = makeStyles((theme: Theme) => ({
  filterDialog: {
    height: theme.spacing(86),
  },
  formBoxBorder: {
    border: StyleDefaults.border,
    borderRadius: StyleDefaults.borderRadius,
  },
  headingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {
    flexGrow: 1,
  },
  divider: {
    backgroundColor: '#e0e0e0',
    width: '1px',
    height: theme.spacing(3),
    display: 'inline-block',
    margin: theme.spacing(1),
  },
  applyButton: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(1),
    height: theme.spacing(5),
    minWidth: theme.spacing(26),
  },
}))

export const FilterCheckboxGroup: React.FC<{
  title: string
  filter: string
  selections: any[] | null | undefined
  values: any[]
  onChange: (select: boolean, filter: string, value: any) => void
}> = ({ title, filter, selections, values, onChange }) => {
  const theme = useTheme()
  const classes = useStyles()

  return (
    <Grid item>
      <Box
        border={2}
        className={classes.formBoxBorder}
        width={theme.spacing(24.5)}
        height={theme.spacing(41)}
        color="text">
        <FormControl component="fieldset">
          <Box paddingX={3} paddingY={3.5}>
            <FormLabel>
              <Typography variant="h6" color="textPrimary">
                {title}
              </Typography>
            </FormLabel>
            <FormGroup>
              {values.map(s => (
                <FormControlLabel
                  key={s.label}
                  label={s.label}
                  control={
                    <Checkbox
                      checked={selections ? selections.includes(s.value) : false}
                      onChange={evt => onChange(evt.target.checked, filter, s.value)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Box>
        </FormControl>
      </Box>
    </Grid>
  )
}

export const IssuerFilter: React.FC<{
  onClose: () => void
  filter: OrderBookFilterInput
  applyFilter: (filter: OrderBookFilterInput) => void
}> = ({ onClose, filter, applyFilter }) => {
  const classes = useStyles()

  const [filterState, setFilterState] = useState<OrderBookFilterInput>(copyFilter(filter))

  // this dialog keeps its own OrderBookFilterInput state which is updated as the user changes the filter;
  // to avoid interference with the state passed in, it is copied when the dialog is entered and a copy is returned
  // (however, it is convenient for the array value references to remain intact, so avoid cloneDeep)
  function copyFilter(filter: OrderBookFilterInput) {
    return {
      valuation: filter.valuation && Array.from(filter.valuation),
      bidVolumeActive: filter.bidVolumeActive && Array.from(filter.bidVolumeActive),
      askVolumeActive: filter.askVolumeActive && Array.from(filter.askVolumeActive),
      tradeStructuresActive: filter.tradeStructuresActive && Array.from(filter.tradeStructuresActive),
    }
  }

  function addOrRemoveSelection<T>(select: boolean, selections: Array<T>, value: T): Array<T> {
    if (select && !selections.includes(value)) {
      selections.push(value)
    } else if (selections.includes(value)) {
      selections = selections.filter(s => s !== value)
    }
    return selections
  }

  const onFilterCheckRange = (select: boolean, filter: string, value: { min: number; max: number }) => {
    switch (filter) {
      case 'valuation':
        const valuation = addOrRemoveSelection(select, filterState?.valuation ?? [], value)
        setFilterState({ ...filterState, valuation })
        break
      case 'bidVolume':
        const bidVolumeActive = addOrRemoveSelection(select, filterState?.bidVolumeActive ?? [], value)
        setFilterState({ ...filterState, bidVolumeActive })
        break
      case 'askVolume':
        const askVolumeActive = addOrRemoveSelection(select, filterState?.askVolumeActive ?? [], value)
        setFilterState({ ...filterState, askVolumeActive })
        break
    }
  }

  const onFilterReset = () => {
    setFilterState({})
  }

  const onFilterStructure = (select: boolean, filter: string, value: ListingStructureType) => {
    const tradeStructuresActive = addOrRemoveSelection(select, filterState?.tradeStructuresActive ?? [], value)
    setFilterState({ ...filterState, tradeStructuresActive })
  }

  return (
    <Dialog open maxWidth={false} className={classes.filterDialog}>
      <MuiDialogTitle disableTypography className={classes.headingContainer}>
        <Typography variant="h6" align="center">
          Filter
        </Typography>
        <span className={classes.spacer} />
        <IconButton aria-label="reset" onClick={onFilterReset}>
          <SyncIcon />
        </IconButton>
        <span className={classes.divider} />
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <Divider />
      <DialogContent>
        <Grid container justify="center" spacing={3}>
          <FilterCheckboxGroup
            title="Valuation"
            filter="valuation"
            selections={filterState.valuation}
            values={valuationSize}
            onChange={onFilterCheckRange}
          />
          <FilterCheckboxGroup
            title="Bid Volume"
            filter="bidVolume"
            selections={filterState.bidVolumeActive}
            values={volumeSize}
            onChange={onFilterCheckRange}
          />
          <FilterCheckboxGroup
            title="Ask Volume"
            filter="askVolume"
            selections={filterState.askVolumeActive}
            values={volumeSize}
            onChange={onFilterCheckRange}
          />
          <FilterCheckboxGroup
            title="Structures"
            filter="structures"
            selections={filterState.tradeStructuresActive}
            values={Object.values(ListingStructureType).map(e => ({ label: listingStructureDisplayName(e), value: e }))}
            onChange={onFilterStructure}
          />
        </Grid>
        <Grid container justify="center">
          <Button
            variant="contained"
            color="primary"
            className={classes.applyButton}
            onClick={() => {
              applyFilter(copyFilter(filterState))
            }}>
            Apply
          </Button>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
